'use client';

import { Avatar, AvatarImage } from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { Progress } from '@ahafood/ui/components/progress';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix, type Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import dayjs from 'dayjs';
import { ArrowRight, Gift, MapPin, Zap } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useParams, useRouter } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import PromoIcons from './PromoIcons';
import SuperDealSection from './super-deal-section';
import type { CampaignBanner } from '@/api/ahafood/campaign-banner';
import {
  useGetDealProgress,
  type Progress as ApiProgress,
} from '@/api/use-get-deal-progress';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { LoginDialog } from '@/app/[locale]/(main)/store/components/fb-login';
import { FreeDragCarousel } from '@/components/carousel/free-drag-carousel';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { DateFormat, formatUnixTime } from '@/lib/datetime';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import type { MenuItem } from '@/types/item';

export type NewStoreDealProps = {
  cityId: string;
  citySlug: string;
  /** Banner type=HOT_DEAL/SUPER_DEAL (đã lọc từ API mới) — nguồn Super Deal. */
  superDeals?: CampaignBanner[];
  /** Đang fetch campaign-banners (client) → hiện skeleton dải Super Deal. */
  superDealsLoading?: boolean;
};

const formatTime = (timestamp: number) => {
  return formatUnixTime(timestamp, DateFormat.TIME_24);
};

const getTimeRange = (store_open_hours: any) => {
  if (!store_open_hours) return '06:00 - 22:00';

  const today = new Date().getDay();
  const dayKey = today === 0 ? '6' : (today - 1).toString();

  const todayHours = store_open_hours[dayKey];
  if (!todayHours || todayHours.length === 0) return 'Đóng cửa';

  const firstSlot = todayHours[0];
  const lastSlot = todayHours[todayHours.length - 1];

  return `${formatTime(firstSlot.start_time)} - ${formatTime(
    lastSlot.end_time
  )}`;
};

export default function NewStoreDeal({
  cityId,
  citySlug,
  superDeals,
  superDealsLoading = false,
}: NewStoreDealProps) {
  // Get locale and city from route params
  const params = useParams();
  const rawCity = Array.isArray(params.city) ? params.city[0] : params.city;
  const router = useRouter();
  const { user, isAuthenticated } = useAuthStore();
  const isMobile = useIsMobile();
  const goToOrderWebstore = useGoToOrderWebstore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const address = useAiFoodOrderStore.use.address() as Address | null;
  const routes = useCityRoutes();

  // Fetch deal progress
  const { data: dealProgressData, isLoading } = useGetDealProgress({
    city_id: cityId,
    skip: 0,
    limit: 25,
    ...(address?.lat && address?.lng
      ? {
          lat: address.lat,
          lng: address.lng,
        }
      : {}),
  });

  const deal = superDeals ?? [];
  const deal_progress = dealProgressData?.progress;

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  const convertToMenuItem = useCallback((dealItem: ApiProgress): MenuItem => {
    return {
      _id: dealItem.item_id,
      name: dealItem.item_name,
      price: dealItem.price,
      discounted_price: dealItem.discounted_price,
      image_thumb_url: dealItem.item_image_url || '',
      is_ai_image: dealItem.is_ai_image ?? false,
    };
  }, []);

  const handleOrderNow = useCallback(
    async (dealItem: ApiProgress) => {
      try {
        trackEvent('click_button', {
          action_time: Math.floor(Date.now() / 1000),
          is_login: isAuthenticated ? 'true' : 'false',
          event_timestamp: dayjs().unix(),
          button_name: 'order_now',
          city_id: cityId,
          button_location: 'flash_deal',
          location_text: address?.address,
          lat: address?.lat,
          lng: address?.lng,
          fb_id: user?.id,
          store_name: dealItem.store_name,
          store_id: dealItem.store_id.toString(),
          store_page_id: dealItem.page_id.toString(),
        });

        const status = dealItem.status;
        if (status === 'OUT_OF_STOCK' || status === 'INACTIVE') {
          router.push(routes.storeDetail(dealItem.store_slug));
          return;
        }

        // Use goToOrderWebstore to open sheet and navigate to store detail
        await goToOrderWebstore(
          dealItem.page_id.toString(),
          dealItem.store_slug,
          '',
          convertToMenuItem(dealItem),
          dealItem.store_id?.toString() || ''
        );
        router.push(routes.storeDetail(dealItem.store_slug));
      } catch (error) {
        console.error('Failed to go to order webstore:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuthenticated,
      cityId,
      address,
      user?.id,
      router,
      goToOrderWebstore,
      convertToMenuItem,
    ]
  );

  const handleClick = useCallback(
    (dealItem: ApiProgress) => {
      if (!isAuthenticated) {
        setShowLoginDialog(true);
      } else {
        void handleOrderNow(dealItem);
      }
    },
    [isAuthenticated, handleOrderNow]
  );

  const { displayedDeals } = useMemo(() => {
    const deals = deal_progress ?? [];
    // Hide unavailable deals
    const availableDeals = deals.filter(
      (dealItem) => dealItem?.is_available !== false
    );
    const maxDisplayItems = 10;

    // Only slice if needed
    const displayed =
      availableDeals.length > maxDisplayItems
        ? availableDeals.slice(0, maxDisplayItems)
        : availableDeals;

    return {
      displayedDeals: displayed,
    };
  }, [deal_progress]);

  const viewMoreDeals = useCallback(() => {
    router.push(`/${rawCity}/danh-sach-cua-hang-co-hot-deal`);
  }, [rawCity, router]);

  const renderDealBadge = useCallback((dealItem: ApiProgress) => {
    const { discount_config } = dealItem;

    let badgeText = 'Deal';
    if (discount_config.type === 'VALUE') {
      badgeText = `Giảm ${discount_config.value.toLocaleString()}đ`;
    } else if (discount_config.type === 'FLAT') {
      badgeText = `Đồng giá ${discount_config.value.toLocaleString()}đ`;
    } else if (discount_config.type === 'PERCENT') {
      badgeText = `Giảm ${discount_config.value}%`;
    }

    return (
      <div className="absolute top-2 left-2 z-30 pointer-events-none">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-red-50 to-[#FF8C00] text-white text-[10px] md:text-xs font-black uppercase tracking-wider rounded-xl shadow-sm border border-white/30">
          <Zap className="size-2 fill-white" />
          {badgeText}
        </div>
      </div>
    );
  }, []);

  const renderDealCard = useCallback(
    (dealItem: ApiProgress) => {
      const timeRange = getTimeRange(dealItem.store_open_hours);
      const status = dealItem.status;

      // Ưu tiên daily cap (max_per_day) — nếu có cap daily, hiển thị progress
      // theo "hôm nay". Else fallback lifetime cap (max_use). Else unlimited.
      const hasDailyCap = dealItem.max_per_day !== -1;
      const hasLifetimeCap = dealItem.max_use !== -1;
      const isUnlimited = !hasDailyCap && !hasLifetimeCap;
      const capTotal = hasDailyCap ? dealItem.max_per_day : dealItem.max_use;
      const capUsed = hasDailyCap
        ? dealItem.per_day_usage_count
        : dealItem.usage_count;
      const remaining = isUnlimited ? -1 : capTotal - capUsed;
      const progressPercentage = isUnlimited
        ? 50
        : Math.max(5, (capUsed / capTotal) * 100);
      const dailySuffix = hasDailyCap ? ' hôm nay' : '';

      return (
        <Card
          className={cn(
            'group relative h-full flex flex-col cursor-pointer',
            'backdrop-blur-md',
            'bg-white/10',
            'border border-white/20',
            'rounded-[1.4rem] overflow-hidden shadow-none'
          )}
          onClick={() => handleClick(dealItem)}
        >
          {/* Image Section */}
          <div className="relative aspect-square overflow-hidden rounded-[1.2rem]">
            {renderDealBadge(dealItem)}
            {dealItem.total_items && dealItem.total_items > 1 && (
              <div className="absolute top-10 left-2 z-30 px-2 py-1 bg-white/90 text-[#FF6A1E] font-bold text-[10px] md:text-xs rounded-lg shadow-sm border border-white/50 flex items-center">
                <Gift className="size-3 mr-1" />+{dealItem.total_items - 1}
                món
              </div>
            )}

            {/* Top Right Badges */}
            <div className="absolute top-2 right-2 z-30 flex flex-col gap-1.5">
              {dealItem.is_ai_image && (
                <div className="size-6 bg-white/90 rounded-lg shadow-sm border border-white/50 flex items-center justify-center p-1">
                  <Image
                    className="size-4"
                    src={`${assetPrefix}/static/icons/white-ai.svg`}
                    alt="AI"
                    style={{
                      filter:
                        'invert(45%) sepia(87%) saturate(1418%) hue-rotate(338deg) brightness(98%) contrast(101%)',
                    }}
                  />
                </div>
              )}
            </div>

            <Avatar className="size-full rounded-none">
              <AvatarImage
                src={dealItem.item_image_url || dealItem.store_image_url || ''}
                alt={dealItem.item_name || 'Deal image'}
                className="object-cover"
                data-testid="home-flash-deal-card-image"
              />
            </Avatar>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent z-50 rounded-[1.2rem]" />
            <div className="absolute bottom-2 inset-x-3 z-[60] flex items-end justify-between">
              <div className="flex flex-col">
                {dealItem.discounted_price ? (
                  <>
                    <span className="text-white text-base md:text-xl font-black leading-none drop-shadow-lg">
                      {dealItem.discounted_price.toLocaleString()}đ
                    </span>
                    <span
                      className="text-white/60 text-[10px] font-bold line-through drop-shadow-md mt-0.5"
                      data-testid="home-flash-deal-card-origin-price"
                    >
                      {dealItem.price.toLocaleString()}đ
                    </span>
                  </>
                ) : (
                  <span className="text-white text-base md:text-xl font-black drop-shadow-lg">
                    {dealItem.price.toLocaleString()}đ
                  </span>
                )}
              </div>
              <div className="flex items-end text-white/90 text-[10px] flex-col font-bold">
                <span className="truncate flex items-center">
                  <MapPin className="size-2.5 mr-1 " />
                  {dealItem.distance && dealItem.distance > 0 && (
                    <span>{`${(dealItem.distance / 1000).toFixed(1)} km`}</span>
                  )}{' '}
                </span>
                <span>{timeRange}</span>
              </div>
            </div>

            {(status === 'INACTIVE' || status === 'OUT_OF_STOCK') && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[70]">
                <div className="bg-white/90 px-4 py-2 rounded-2xl shadow-md">
                  <p className="text-neutral-80 font-black text-xs uppercase tracking-widest">
                    Đã hết deal
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Content Section — sát bottom image, không gap */}
          <div className="p-2 flex flex-col gap-1.5 z-10">
            <div>
              <h2 className="text-sm md:text-base font-bold text-white line-clamp-1">
                {dealItem.item_name}
              </h2>
              <div className="flex items-center text-[11px] md:text-xs text-white/70 font-medium truncate">
                {dealItem.store_name}
              </div>
            </div>

            <div className="relative">
              <Progress
                indicatorClassName={
                  isUnlimited
                    ? 'bg-gradient-to-r from-red-50 to-primary-50'
                    : undefined
                }
                value={progressPercentage}
                className="h-2 md:h-2.5 bg-white/40"
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 z-10"
                style={{
                  left: `calc(${progressPercentage}% - 10px)`,
                }}
              >
                <Zap className="size-5 fill-yellow-20 text-yellow-30" />
              </div>
            </div>

            <span className="text-xs font-semibold text-white/80">
              {isUnlimited
                ? 'Đang hot 🔥'
                : `Còn ${remaining}/${capTotal} đơn${dailySuffix}`}
            </span>
          </div>
        </Card>
      );
    },
    [handleClick, renderDealBadge]
  );

  // Move loading checks here for better control — match Flash Deal orange gradient + card layout
  if (isLoading || superDealsLoading || !cityId) {
    return (
      <StoreListWrapper>
        <SuperDealSection
          deals={[]}
          isLoading
          payload={{ city_id: cityId }}
          city_slug={citySlug}
        />
        <div className="relative flex flex-col gap-4 py-8 bg-gradient-to-br from-[#FF7F32] via-[#FF6A1E] to-[#FF3A1E] overflow-hidden md:rounded-2xl">
          <div className="px-4 flex items-center justify-between">
            <Skeleton className="h-7 w-40 bg-white/30" />
            <Skeleton className="size-9 rounded-full bg-white/30" />
          </div>
          <div className="flex flex-row flex-nowrap gap-3 px-4 overflow-x-hidden">
            {[...Array(isMobile ? 2 : 3)].map((_, index) => (
              <div
                key={`skeleton-loading-${index}`}
                className="basis-[160px] md:basis-[272px] shrink-0 flex flex-col gap-2 rounded-[1.4rem] border border-white/20 bg-white/10 p-1"
              >
                <Skeleton className="aspect-square w-full rounded-[1.2rem] bg-white/30" />
                <div className="px-2 py-1 flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-3/4 bg-white/30" />
                  <Skeleton className="h-2 w-full rounded-full bg-white/30" />
                  <Skeleton className="h-3 w-1/2 bg-white/30" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </StoreListWrapper>
    );
  }

  // If no data at all for both sections, then return null
  const hasSuperDeals = deal && deal.length > 0;
  const hasFlashDeals = displayedDeals && displayedDeals.length > 0;

  if (!hasSuperDeals && !hasFlashDeals) return null;

  return (
    <StoreListWrapper>
      <div className="flex flex-col md:gap-20 gap-0">
        {/* Super Deal — full-width edge-to-edge (no px-4 wrapper) */}
        {hasSuperDeals && (
          <SuperDealSection
            deals={deal}
            payload={{ city_id: cityId }}
            city_slug={citySlug}
          />
        )}

        {/* Flash Deal — full-width edge-to-edge, padding chỉ ở header/footer */}
        {hasFlashDeals && (
          <div className="relative flex flex-col gap-4 py-8 bg-gradient-to-br from-[#FF7F32] via-[#FF6A1E] to-[#FF3A1E] overflow-hidden md:rounded-2xl">
            <PromoIcons />

            <div className="relative z-20 flex items-center gap-2 md:gap-3 w-full justify-between px-4">
              <div className="flex flex-col">
                <Image
                  src={'/static/icons/flash-deal.svg'}
                  alt="flash-deal-title"
                  width={180}
                  height={31}
                  className="object-contain object-bottom"
                />
                <p
                  className="text-white/80 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] mt-1 ml-1"
                  data-testid="home-flash-deal-title"
                >
                  Săn deal hấp dẫn
                </p>
              </div>
              <Button
                size="icon"
                variant="link"
                onClick={() => {
                  viewMoreDeals();
                  trackEvent('click_button', {
                    action_time: Math.floor(Date.now() / 1000),
                    button_name: 'flash_deal_toggle',
                    button_location: 'flash_deal_section',
                  });
                }}
                className="flex items-center size-9 text-xl font-bold text-[#FF6A1E] bg-white/90 rounded-full shadow-md"
              >
                <ArrowRight className="size-5" />
              </Button>
            </div>
            <FreeDragCarousel
              key={`deals-carousel-${displayedDeals
                .map((deal, idx) => `${deal.item_id}-${idx}`)
                .join('-')}`}
              className="w-full md:h-auto z-20"
              contentClassName="ml-0 gap-3 px-4 items-stretch"
            >
              {displayedDeals.map((dealItem, index) => (
                <FreeDragCarousel.Item
                  key={`deal-${dealItem.item_id}-${dealItem.usage_count}-${index}`}
                  className="basis-[160px] md:basis-[272px] pl-0"
                >
                  <div className="relative h-full py-2">
                    {renderDealCard(dealItem)}
                  </div>
                </FreeDragCarousel.Item>
              ))}

              {/* "Xem tất cả" — arrow card ở cuối carousel */}
              <FreeDragCarousel.Item className="basis-auto pl-0">
                <button
                  type="button"
                  onClick={() => {
                    viewMoreDeals();
                    trackEvent('click_button', {
                      action_time: Math.floor(Date.now() / 1000),
                      button_name: 'flash_deal_toggle',
                      button_location: 'flash_deal_section',
                    });
                  }}
                  aria-label="Xem tất cả Flash Deal"
                  className="h-full flex items-center justify-center px-2"
                >
                  <div className="size-12 rounded-full bg-white text-[#FF6A1E] shadow-md flex items-center justify-center hover:opacity-90 transition-opacity">
                    <ArrowRight className="size-5" />
                  </div>
                </button>
              </FreeDragCarousel.Item>
            </FreeDragCarousel>
          </div>
        )}
      </div>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="flash_deal"
      />
    </StoreListWrapper>
  );
}

export function StoreListWrapper({
  children,
  payload,
}: PropsWithChildren & { payload?: any }) {
  const campaignParam = payload?.campaign_id;

  return (
    <section className="ahf-deal relative overflow-hidden bg-transparent">
      <div
        className={cn(
          'relative z-[1] mx-auto max-w-screen-xl gap-y-6 md:px-0 md:py-8',
          {
            'pb-10 md:pb-16': campaignParam,
          }
        )}
      >
        {children}
      </div>
    </section>
  );
}
